import { z } from 'zod'

import { occasionType } from './product.js'

/**
 * 수신자 디지털 화환 카드 (BIZ-86).
 *
 * 디자인은 "프레임(코드) + 스킨(데이터)" 구조다. 프레임(PAPER=종이 카드형,
 * NIGHT=풀스크린 무드형)은 web이 그리고, 스킨(색·배경·기본문구)은 운영자가
 * admin에서 관리한다. 발신자는 주문 시 스킨을 고르고 메시지를 쓴다.
 * 수신자는 배송완료 후 알림톡/문자로 받은 공개 링크(무인증)로 카드를 본다.
 */

export const cardFrame = z.enum(['PAPER', 'NIGHT', 'IMAGE'])
export type CardFrame = z.infer<typeof cardFrame>

/** 텍스트 위치 프리셋 (IMAGE 프레임 전용) — 배경 아트에 맞춰 상단/중앙/하단. PAPER/NIGHT는 무시. */
export const cardTextPosition = z.enum(['TOP', 'CENTER', 'BOTTOM'])
export type CardTextPosition = z.infer<typeof cardTextPosition>

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, '#RRGGBB 형식의 색상값을 입력하세요')

/** 카드 스킨 한 벌 (운영자 관리 + 발신자 선택 UI 공용). */
export const cardSkinResponse = z.object({
  id: z.number().int(),
  name: z.string(),
  occasionType,
  frame: cardFrame,
  bgColor: z.string(),
  textColor: z.string(),
  accentColor: z.string(),
  /** 배경 아트 표시용 URL — 업로드면 서명 URL, 외부면 그 URL. */
  bgImageUrl: z.string().nullable(),
  /** 업로드로 저장된 배경 아트 키(있으면). 수정 시 서명 URL을 되쏘지 않고 키로 왕복. */
  bgImageKey: z.string().nullable(),
  /** 텍스트 위치 프리셋(IMAGE 프레임 전용). */
  textPosition: cardTextPosition,
  defaultMessage: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
})
export type CardSkinResponse = z.infer<typeof cardSkinResponse>

export const cardSkinListResponse = z.object({ items: z.array(cardSkinResponse) })
export type CardSkinListResponse = z.infer<typeof cardSkinListResponse>

/** 운영자 스킨 등록·수정 (BIZ-86 admin). */
export const saveCardSkinRequest = z.object({
  name: z.string().trim().min(1).max(50),
  occasionType,
  frame: cardFrame,
  bgColor: hexColor,
  textColor: hexColor,
  accentColor: hexColor,
  /** 업로드 배경 아트 키(기본). 외부 URL(bgImageUrl)도 시드·레거시용으로 허용. */
  bgImageKey: z.string().min(1).max(512).optional(),
  textPosition: cardTextPosition.optional(),
  bgImageUrl: z
    .preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), z.string().url().max(2048).nullable())
    .optional(),
  defaultMessage: z
    .preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), z.string().max(300).nullable())
    .optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(999).optional(),
})
export type SaveCardSkinRequest = z.infer<typeof saveCardSkinRequest>

/**
 * 공개 카드 뷰 (무인증, 토큰 URL). 수신자가 보는 전부 — 개인정보 최소화:
 * 주문번호·회사·배송지·연락처는 싣지 않는다. 발신자명·문구만.
 */
export const publicCardResponse = z.object({
  occasionType,
  frame: cardFrame,
  skin: z.object({
    bgColor: z.string(),
    textColor: z.string(),
    accentColor: z.string(),
    bgImageUrl: z.string().nullable(),
    /** 텍스트 위치 프리셋(IMAGE 프레임 전용). */
    textPosition: cardTextPosition,
  }),
  /** 리본 문구(근조·축하) 또는 실물 카드 문구 1행(난·기타). 카드 헤드라인. */
  headline: z.string().nullable(),
  /** 발신자가 쓴 카드 메시지(여러 줄). */
  message: z.string().nullable(),
  senderName: z.string().nullable(),
})
export type PublicCardResponse = z.infer<typeof publicCardResponse>


